import { Injectable } from '@angular/core';
import { CompanyFilter } from '@models/commons/company-filter';
import { Company } from '@models/company/company';
import { Field } from '@models/company/field';
import { Facet } from '@models/search/facet';
import { FieldEnum } from '@models/search/field.enum';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import {
  Filter,
  FilterFailed,
  FilterSuccess,
  InitFields,
  LoadMore,
  LoadMoreFailed,
  LoadMoreSuccess,
} from '@store/search/companies/search-companies.actions';
import { CompanySearchWebservice } from '@webservices/search-api/company-search.webservice';
import { catchError, switchMap } from 'rxjs/operators';

export interface SearchCompaniesStateModel {
  companies: Company[];
  fieldFacets: Facet<Field>[];
  filter: CompanyFilter;

  loading: boolean;
  total: number;
  pages: number;
  page: number;
  error: any;
}

const defaultState: SearchCompaniesStateModel = {
  companies: [],
  fieldFacets: [],
  filter: new CompanyFilter(),

  loading: false,
  total: 0,
  pages: 0,
  page: 0,
  error: null,
};

@State<SearchCompaniesStateModel>({
  name: 'searchCompanies',
  defaults: defaultState,
})
@Injectable()
export class SearchCompaniesState {
  @Selector()
  static companies(state: SearchCompaniesStateModel): any {
    return state.companies;
  }

  @Selector()
  static fieldFacets(state: SearchCompaniesStateModel): any {
    return state.fieldFacets;
  }

  @Selector()
  static page(state: SearchCompaniesStateModel): any {
    return state.page;
  }

  @Selector()
  static pages(state: SearchCompaniesStateModel): any {
    return state.pages;
  }

  @Selector()
  static total(state: SearchCompaniesStateModel): any {
    return state.total;
  }

  @Selector()
  static loading(state: SearchCompaniesStateModel): any {
    return state.loading;
  }

  @Selector()
  static filter(state: SearchCompaniesStateModel): any {
    return state.filter;
  }

  constructor(private readonly companySearchWebservice: CompanySearchWebservice) {}

  @Action(Filter, { cancelUncompleted: true })
  filter(ctx: StateContext<SearchCompaniesStateModel>, { keywords, city, fieldFacets, page }: Filter): any {
    const companyFilter = new CompanyFilter({
      keywords,
      geoPoint: city ? city.geo : null,
      fields: fieldFacets.map((item) => item.model),
      page,
    });

    ctx.patchState({
      filter: companyFilter,
      page,
      loading: true,
    });

    return this.companySearchWebservice.getBy(companyFilter).pipe(
      switchMap((result) => ctx.dispatch(new FilterSuccess(result))),
      catchError((error) => ctx.dispatch(new FilterFailed(error)))
    );
  }

  @Action(FilterSuccess)
  filterSuccess(ctx: StateContext<SearchCompaniesStateModel>, { result }: FilterSuccess): any {
    return ctx.patchState({
      loading: false,
      companies: result.hits,
      pages: result.nbPages,
      page: result.page,
      total: result.nbHits,
    });
  }

  @Action(LoadMore)
  loadMore(ctx: StateContext<SearchCompaniesStateModel>, { keywords, geoPoint, fieldFacets, page }: LoadMore): any {
    if (!ctx.getState().loading) {
      const companyFilter = new CompanyFilter({
        keywords,
        geoPoint: geoPoint ? geoPoint : null,
        fields: fieldFacets.map((item) => item.model),
        page,
      });

      ctx.patchState({
        filter: companyFilter,
        page,
        loading: true,
      });

      return this.companySearchWebservice.getBy(companyFilter).pipe(
        switchMap((result) => ctx.dispatch(new LoadMoreSuccess(result))),
        catchError((error) => ctx.dispatch(new LoadMoreFailed(error)))
      );
    }
  }

  @Action(LoadMoreSuccess)
  loadMoreSuccess(ctx: StateContext<SearchCompaniesStateModel>, { result }: LoadMoreSuccess): any {
    return ctx.setState(
      patch<SearchCompaniesStateModel>({
        loading: false,
        companies: append(result.hits),
        pages: result.nbPages,
        page: result.page,
        total: result.nbHits,
      })
    );
  }

  @Action([FilterFailed, LoadMoreFailed])
  loadFailed(ctx: StateContext<SearchCompaniesStateModel>, { error }: LoadMoreFailed): any {
    ctx.patchState({ error, loading: false });
  }

  @Action(InitFields)
  initFields(ctx: StateContext<SearchCompaniesStateModel>): any {
    return ctx.patchState({
      fieldFacets: Object.values(FieldEnum)
        .map((fieldId) => new Field({ id: fieldId, titleShort: fieldId }))
        .map((field) => new Facet({ model: field })),
    });
  }
}
